<template>
  <Card class="mb-8 border-orange-100">
    <template #title>
      <div class="flex items-center gap-2 text-gray-800">
        <i class="pi pi-wrench text-orange-500"></i>
        Tenant Migrations
      </div>
    </template>
    <template #content>
      <p class="text-gray-600 text-sm mb-6">
        Run roles and features migrations for <strong>{{ tenantSlug }}</strong> without opening
        Content Management. Use dry run first; apply requires a reason (logged in platform audit).
        CM Developer → Migrations remains available as a fallback.
      </p>

      <!-- Roles -->
      <div class="border border-gray-200 rounded-lg p-4 mb-4">
        <h4 class="font-semibold text-gray-800 mb-2">Tenant roles (legacy config)</h4>
        <p class="text-sm text-gray-600 mb-3">
          Grants <code class="bg-gray-100 px-1 rounded">admin</code> /
          <code class="bg-gray-100 px-1 rounded">companySuperuser</code> on
          <code class="bg-gray-100 px-1 rounded">User.roles</code> from
          <code class="bg-gray-100 px-1 rounded">admin_emails</code>,
          <code class="bg-gray-100 px-1 rounded">admin_IDs</code>, and company superuser lists.
        </p>
        <div class="flex flex-wrap gap-2 mb-3">
          <Button
            label="Dry run"
            icon="pi pi-search"
            severity="info"
            :loading="rolesLoading"
            @click="runRolesDryRun"
          />
          <Button
            label="Apply"
            icon="pi pi-check"
            severity="success"
            :loading="rolesLoading"
            :disabled="!rolesDryRun"
            @click="openApplyDialog('roles')"
          />
          <Button
            v-if="rolesResult"
            label="Clear"
            icon="pi pi-times"
            severity="secondary"
            outlined
            :disabled="rolesLoading"
            @click="rolesResult = null; rolesDryRun = null"
          />
        </div>
        <div v-if="rolesError" class="text-red-600 text-sm mb-2">{{ rolesError }}</div>
        <div v-if="rolesResult" class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm bg-gray-50 p-3 rounded">
          <div v-for="(row, i) in tenantRolesRows(rolesResult)" :key="'roles-' + i">
            <div class="text-gray-500 text-xs">{{ row.label }}</div>
            <div class="font-semibold">{{ row.value }}</div>
          </div>
        </div>
      </div>

      <!-- Features -->
      <div class="border border-gray-200 rounded-lg p-4 mb-4">
        <h4 class="font-semibold text-gray-800 mb-2">Features collection cleanup</h4>
        <p class="text-sm text-gray-600 mb-3">
          Normalizes legacy feature rows, dedupes by legacy type, clears overlay routes, and
          creates missing registered overlays (e.g. Development Tips).
        </p>
        <div class="flex flex-wrap gap-2 mb-3">
          <Button
            label="Dry run"
            icon="pi pi-search"
            severity="info"
            :loading="featuresLoading"
            @click="runFeaturesDryRun"
          />
          <Button
            label="Apply"
            icon="pi pi-check"
            severity="success"
            :loading="featuresLoading"
            :disabled="!featuresDryRun"
            @click="openApplyDialog('features')"
          />
          <Button
            v-if="featuresResult"
            label="Clear"
            icon="pi pi-times"
            severity="secondary"
            outlined
            :disabled="featuresLoading"
            @click="featuresResult = null; featuresDryRun = null"
          />
        </div>
        <div v-if="featuresError" class="text-red-600 text-sm mb-2">{{ featuresError }}</div>
        <div v-if="featuresResult" class="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-gray-50 p-3 rounded">
          <div v-for="(row, i) in featuresRows(featuresResult)" :key="'feat-' + i">
            <div class="text-gray-500 text-xs">{{ row.label }}</div>
            <div class="font-semibold">{{ row.value }}</div>
          </div>
        </div>
      </div>

      <!-- Customer layouts seed -->
      <div class="border border-gray-200 rounded-lg p-4">
        <h4 class="font-semibold text-gray-800 mb-2">Customer navigation &amp; home layout seed</h4>
        <p class="text-sm text-gray-600 mb-3">
          Seeds default navigation/home layouts when missing and syncs feature
          <code class="bg-gray-100 px-1 rounded">catalogKey</code> values from routes.
          Run after features migration when CM navigation is empty or broken.
        </p>
        <div class="flex flex-wrap gap-2 mb-3">
          <Button
            label="Dry run"
            icon="pi pi-search"
            severity="info"
            :loading="layoutsLoading"
            @click="runLayoutsDryRun"
          />
          <Button
            label="Apply"
            icon="pi pi-check"
            severity="success"
            :loading="layoutsLoading"
            :disabled="!layoutsDryRun"
            @click="openApplyDialog('layouts')"
          />
          <Button
            v-if="layoutsResult"
            label="Clear"
            icon="pi pi-times"
            severity="secondary"
            outlined
            :disabled="layoutsLoading"
            @click="layoutsResult = null; layoutsDryRun = null"
          />
        </div>
        <div v-if="layoutsError" class="text-red-600 text-sm mb-2">{{ layoutsError }}</div>
        <div v-if="layoutsResult" class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm bg-gray-50 p-3 rounded">
          <div v-for="(row, i) in layoutsRows(layoutsResult)" :key="'lay-' + i">
            <div class="text-gray-500 text-xs">{{ row.label }}</div>
            <div class="font-semibold">{{ row.value }}</div>
          </div>
        </div>
      </div>
    </template>
  </Card>

  <Dialog
    v-model:visible="applyDialogVisible"
    :header="applyDialogTitle"
    modal
    :style="{ width: '28rem' }"
  >
    <p class="text-sm text-gray-600 mb-4">{{ applyDialogMessage }}</p>
    <label class="block text-sm font-medium text-gray-700 mb-2">
      Reason <span class="text-red-500">*</span>
    </label>
    <Textarea v-model="applyReason" rows="3" class="w-full" placeholder="Why is this migration being applied?" />
    <template #footer>
      <Button label="Cancel" severity="secondary" text @click="applyDialogVisible = false" />
      <Button
        label="Apply"
        icon="pi pi-check"
        severity="danger"
        :loading="applyInProgress"
        :disabled="!applyReason.trim()"
        @click="confirmApply"
      />
    </template>
  </Dialog>
</template>

<script>
import { platformApi } from '@/services/platformApi';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';

export default {
  name: 'TenantMigrationsSection',
  components: {
    Card,
    Button,
    Dialog,
    Textarea,
  },
  props: {
    tenantSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      rolesLoading: false,
      rolesError: null,
      rolesDryRun: null,
      rolesResult: null,
      featuresLoading: false,
      featuresError: null,
      featuresDryRun: null,
      featuresResult: null,
      layoutsLoading: false,
      layoutsError: null,
      layoutsDryRun: null,
      layoutsResult: null,
      applyDialogVisible: false,
      applyTarget: null,
      applyReason: '',
      applyInProgress: false,
    };
  },
  computed: {
    applyDialogTitle() {
      const titles = {
        roles: 'Apply tenant roles migration',
        features: 'Apply features migration',
        layouts: 'Apply customer layout seed',
      };
      return titles[this.applyTarget] || 'Apply migration';
    },
    applyDialogMessage() {
      const messages = {
        roles: `This updates User.roles for tenant "${this.tenantSlug}" from legacy configuration lists.`,
        features: `This normalizes and may delete duplicate feature documents for "${this.tenantSlug}".`,
        layouts: `This writes navigation/home layout defaults for "${this.tenantSlug}" when missing.`,
      };
      return messages[this.applyTarget] || '';
    },
  },
  methods: {
    tenantRolesRows(r) {
      if (!r || typeof r !== 'object') return [];
      const enumAdded = Array.isArray(r.enumValuesAdded) ? r.enumValuesAdded : [];
      return [
        { label: 'Dry run', value: r.dryRun ? 'Yes' : 'No' },
        {
          label: 'Admin config entries',
          value: (r.adminEmailsProcessed ?? 0) + (r.adminIdsProcessed ?? 0),
        },
        {
          label: 'Superuser config entries',
          value: (r.superuserEmailsProcessed ?? 0) + (r.superuserIdsProcessed ?? 0),
        },
        { label: 'Users gaining admin', value: r.usersWouldUpdateAdmin ?? 0 },
        { label: 'Users gaining superuser', value: r.usersWouldUpdateSuperuser ?? 0 },
        {
          label: 'Enum values added',
          value: enumAdded.length ? enumAdded.join(', ') : '—',
        },
      ];
    },
    featuresRows(r) {
      if (!r) return [];
      const errors = Array.isArray(r.errors) ? r.errors : [];
      const rows = [
        { label: 'Dry run', value: r.dryRun ? 'Yes' : 'No' },
        { label: 'Total rows', value: r.totalRows ?? 0 },
        { label: 'Rows normalized', value: r.rowsNormalized ?? 0 },
        { label: 'Duplicates removed', value: r.duplicateRowsDeleted ?? 0 },
        { label: 'Overlay routes cleared', value: r.overlayRoutesCleared ?? 0 },
        { label: 'Overlays created', value: r.overlayFeaturesCreated ?? 0 },
        { label: 'Catalog features created', value: r.catalogFeaturesCreated ?? 0 },
        { label: 'Errors', value: errors.length },
      ];
      if (Array.isArray(r.missingCatalogKeys) && r.missingCatalogKeys.length) {
        rows.push({
          label: 'Missing catalog keys',
          value: r.missingCatalogKeys.join(', '),
        });
      }
      return rows;
    },
    layoutsRows(r) {
      if (!r) return [];
      return [
        { label: 'Dry run', value: r.dryRun ? 'Yes' : 'No' },
        { label: 'Navigation seeded', value: r.navigationLayoutSeeded ? 'Yes' : 'No' },
        { label: 'catalogKey synced', value: r.featuresCatalogKeyUpdated ?? 0 },
      ];
    },
    async runRolesDryRun() {
      this.rolesLoading = true;
      this.rolesError = null;
      try {
        const res = await platformApi.migrateTenantRoles(this.tenantSlug, { dryRun: true });
        this.rolesDryRun = res?.data ?? res;
        this.rolesResult = this.rolesDryRun;
        this.toast('success', 'Roles dry run completed');
      } catch (e) {
        this.rolesError = e?.response?.data?.message || e?.message || 'Request failed';
        this.toast('error', 'Roles dry run failed', this.rolesError);
      } finally {
        this.rolesLoading = false;
      }
    },
    async runFeaturesDryRun() {
      this.featuresLoading = true;
      this.featuresError = null;
      try {
        const res = await platformApi.migrateTenantFeatures(this.tenantSlug, { dryRun: true });
        this.featuresDryRun = res?.data ?? res;
        this.featuresResult = this.featuresDryRun;
        this.toast('success', 'Features dry run completed');
      } catch (e) {
        this.featuresError = e?.response?.data?.message || e?.message || 'Request failed';
        this.toast('error', 'Features dry run failed', this.featuresError);
      } finally {
        this.featuresLoading = false;
      }
    },
    async runLayoutsDryRun() {
      this.layoutsLoading = true;
      this.layoutsError = null;
      try {
        const res = await platformApi.seedTenantCustomerLayouts(this.tenantSlug, { dryRun: true });
        this.layoutsDryRun = res?.data ?? res;
        this.layoutsResult = this.layoutsDryRun;
        this.toast('success', 'Layout seed dry run completed');
      } catch (e) {
        this.layoutsError = e?.response?.data?.message || e?.message || 'Request failed';
        this.toast('error', 'Layout seed dry run failed', this.layoutsError);
      } finally {
        this.layoutsLoading = false;
      }
    },
    openApplyDialog(target) {
      this.applyTarget = target;
      this.applyReason = '';
      this.applyDialogVisible = true;
    },
    async confirmApply() {
      const reason = this.applyReason.trim();
      if (!reason) return;
      this.applyInProgress = true;
      try {
        if (this.applyTarget === 'roles') {
          const res = await platformApi.migrateTenantRoles(this.tenantSlug, { dryRun: false, reason });
          this.rolesResult = res?.data ?? res;
          this.toast('success', 'Roles migration applied');
        } else if (this.applyTarget === 'features') {
          const res = await platformApi.migrateTenantFeatures(this.tenantSlug, { dryRun: false, reason });
          this.featuresResult = res?.data ?? res;
          if (res?.success === false) {
            this.featuresError = (res?.data?.errors || []).join('; ') || 'Migration reported errors';
            this.toast('warn', 'Features migration finished with errors');
          } else {
            this.toast('success', 'Features migration applied');
          }
        } else if (this.applyTarget === 'layouts') {
          const res = await platformApi.seedTenantCustomerLayouts(this.tenantSlug, { dryRun: false, reason });
          this.layoutsResult = res?.data ?? res;
          this.toast('success', 'Customer layouts seeded');
        }
        this.applyDialogVisible = false;
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Apply failed';
        this.toast('error', 'Apply failed', msg);
      } finally {
        this.applyInProgress = false;
      }
    },
    toast(severity, summary, detail) {
      this.$toast?.add({ severity, summary, detail, life: 5000 });
    },
  },
};
</script>
